import React from 'react';
import { FacetItem, SearchComboBoxProps } from './../../../types/search';
import {
  Button,
  ComboBox,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  ComboBoxStateContext,
} from 'react-aria-components';

// Uses https://react-spectrum.adobe.com/react-aria/ComboBox.html

function ComboBoxClearButton({ inputRef }: Readonly<{ inputRef?: React.RefObject<HTMLInputElement> }>) {
  const state = React.useContext(ComboBoxStateContext);
  return (
    <Button
      slot={null}
      className="search-combobox__clear-button"
      aria-label="Clear"
      onMouseDown={e => e.preventDefault()}
      onTouchStart={e => e.preventDefault()}
      onPress={() => {
        inputRef.current?.focus();
        state?.setSelectedKey(null);
        state?.setInputValue('');
      }}
    >
      <svg className="search-combobox__clear-button-icon icon" aria-hidden="true">
        <use href="/dist/assets/icons/sprite.svg#x-mark-circle"></use>
      </svg>
    </Button>
  );
}

function getFieldState(selectedKey: string | null | undefined, items: FacetItem[]) {
  const label = items.find(item => item.value === selectedKey)?.label ?? '';
  return {
    selectedKey: selectedKey || null,
    inputValue: label,
    buttonValue: label,
  };
}

export default function SearchComboBox({
  facetKey,
  facetHeading,
  items,
  onChange,
  selectedKey,
}: Readonly<SearchComboBoxProps>) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Deduplicate items by label to prevent duplicates in the dropdown
  const dedupedItems = React.useMemo(() => {
    const itemsByLabel = new Map<string, FacetItem>();
    items.forEach(item => {
      const label = item.label;
      const existing = itemsByLabel.get(label);

      if (!existing) {
        itemsByLabel.set(label, item);
      } else {
        // If current item is selected and existing is not, prefer current
        if (item.total > (existing.total ?? 0)) {
          itemsByLabel.set(label, item);
        }
      }
    });

    return Array.from(itemsByLabel.values());
  }, [items]);

  const [fieldState, setFieldState] = React.useState(() => getFieldState(selectedKey, dedupedItems));

  // Sync fieldState with selectedKey prop changes
  React.useEffect(() => {
    setFieldState(getFieldState(selectedKey, dedupedItems));
  }, [selectedKey, dedupedItems]);
  const [arrowIcon, setArrowIcon] = React.useState('nav-arrow-down');

  // Filter items based on input value
  const filteredItems = React.useMemo(() => {
    if (fieldState.inputValue) {
      return dedupedItems.filter(item => item.label.toLowerCase().includes(fieldState.inputValue.toLowerCase()));
    }
    return dedupedItems;
  }, [fieldState.inputValue, dedupedItems]);

  const handleSelectionChange = (selectedValue: string | null) => {
    const selectedItem = items.find(item => item.value === selectedValue);
    setFieldState({
      selectedKey: selectedValue,
      inputValue: selectedItem?.label ?? '',
      buttonValue: selectedItem?.label ?? '',
    });

    !!selectedValue && onChange(facetKey, selectedValue ?? null);
  };

  const handleInputChange = (value: string) => {
    setFieldState(prevState => ({
      selectedKey: value === '' ? null : prevState.selectedKey,
      inputValue: value,
      buttonValue: value,
    }));
  };

  const handleOpenChange = (isOpen: boolean, menuTrigger: 'manual' | 'focus') => {
    if (isOpen && menuTrigger === 'manual') {
      setFieldState(prevState => ({
        selectedKey: prevState.selectedKey,
        inputValue: '',
        buttonValue: prevState.buttonValue,
      }));

      setArrowIcon('nav-arrow-up');

      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } else {
      setArrowIcon('nav-arrow-down');
    }
  };

  return (
    <ComboBox
      className="search-combobox"
      items={filteredItems}
      selectedKey={fieldState.selectedKey}
      inputValue={fieldState.inputValue}
      onSelectionChange={handleSelectionChange}
      onInputChange={handleInputChange}
      onOpenChange={handleOpenChange}
      allowsEmptyCollection={true}
      menuTrigger="manual"
    >
      <Label className="search-combobox__label">{facetHeading}</Label>
      <Button
        className="search-combobox__toggle-button"
        excludeFromTabOrder={false}
        aria-label={`Search or select a ${facetHeading} option`}
        aria-labelledby={null} // Override react-aria's default behavior
      >
        <span>{fieldState?.buttonValue || 'All'}</span>
        <svg className="search-combobox__toggle-button-icon icon" aria-hidden="true">
          <use href={`/dist/assets/icons/sprite.svg#${arrowIcon}`}></use>
        </svg>
      </Button>
      <div className="search-combobox__search-input-container">
        <svg className="search-combobox__search-input-icon icon" aria-hidden="true">
          <use href="/dist/assets/icons/sprite.svg#search"></use>
        </svg>
        <Input
          ref={inputRef}
          className="search-combobox__search-input"
          placeholder="Type to search"
          aria-label="Search"
        />
        <ComboBoxClearButton inputRef={inputRef} />
      </div>

      <Popover className="search-combobox__popover" shouldFlip={false} offset={0} containerPadding={4}>
        <ListBox className="search-combobox__listbox">
          {filteredItems.length > 0
            ? filteredItems.map(item => (
                <ListBoxItem
                  id={item.value}
                  className="search-combobox__listbox-item"
                  key={item.value}
                  textValue={item.label}
                >
                  {item.label}
                </ListBoxItem>
              ))
            : null}
        </ListBox>
        {filteredItems.length === 0 && (
          <div className="search-combobox__listbox-item search-combobox__listbox-item--no-results">
            No results found.
          </div>
        )}
      </Popover>
    </ComboBox>
  );
}
